/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Line, Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { CircularProgress } from "@mui/material";
import { FaExpand, FaCompress } from "react-icons/fa";
import { format } from "date-fns";
import { apiRequestBodyBuilder } from "../config/tableHelper";
import MetricsModal from "../Tools/ModifyChartColumns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// Specific distinct palette matching the reference image dots/lines
const COLOR_MAP = {
  clicks: {
    border: "#2563EB",
    fillStart: "rgba(37, 99, 235, 0.25)",
    fillEnd: "rgba(37, 99, 235, 0.0)",
  }, // Dark Blue
  average_cpc: {
    border: "#9333EA",
    fillStart: "rgba(147, 51, 234, 0.25)",
    fillEnd: "rgba(147, 51, 234, 0.0)",
  }, // Purple
  ctr: {
    border: "#0D9488",
    fillStart: "rgba(13, 148, 136, 0.25)",
    fillEnd: "rgba(13, 148, 136, 0.0)",
  }, // Teal
  cost: {
    border: "#38BDF8",
    fillStart: "rgba(56, 189, 248, 0.25)",
    fillEnd: "rgba(56, 189, 248, 0.0)",
  }, // Light Blue/Sky
};

const DEFAULT_COLORS = [
  "#2563EB",
  "#9333EA",
  "#0D9488",
  "#38BDF8",
  "#F59E0B",
  "#EF4444",
];

const ALL_METRICS = [
  {
    id: "1",
    title: "Clicks",
    key: "clicks",
    type: "number",
    category: "Performance",
  },
  {
    id: "6",
    title: "Average CPC",
    key: "average_cpc",
    type: "currency",
    category: "Performance",
  },
  {
    id: "4",
    title: "Conversions",
    key: "conversions",
    type: "number",
    category: "Conversions",
  },
  {
    id: "3",
    title: "Cost (₹)",
    key: "cost",
    type: "currency",
    category: "Performance",
  },
  {
    id: "5",
    title: "CTR (%)",
    key: "ctr",
    type: "float",
    category: "Performance",
  },
  {
    id: "11",
    title: "Interaction Rate",
    key: "interaction_rate",
    type: "float",
    category: "Performance",
  },
  {
    id: "91",
    title: "Avg. Cost",
    key: "average_cost",
    type: "currency",
    category: "Performance",
    visible: false,
  },
  {
    id: "8",
    title: "Cost/Conv",
    key: "cost_per_conversion",
    type: "currency",
    category: "Conversions",
  },
  {
    id: "2",
    title: "Impr.",
    key: "impressions",
    type: "number",
    category: "Performance",
  },
  {
    id: "90",
    title: "Avg. CPM",
    key: "average_cpm",
    type: "currency",
    category: "Performance",
    visible: false,
  },
  {
    id: "92",
    title: "Avg. CPV",
    key: "average_cpv",
    type: "currency",
    category: "Performance",
    visible: false,
  },
];

const calcDaysBetweenDates = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return Math.ceil(Math.abs(d2 - d1) / (1000 * 60 * 60 * 24));
};

const ChartPage = ({ customerId, state }) => {
  const chartRef = useRef(null);
  const [selectedMetrics, setSelectedMetrics] = useState([
    "clicks",
    "average_cpc",
    "ctr",
    "cost",
  ]);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [percentageByDate, setPercentageByDate] = useState({});

  const [groupType, setGroupType] = useState("");
  const [chartType, setChartType] = useState("line");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const adjustMenuRef = useRef(null);
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);

  const totalDays = calcDaysBetweenDates(
    format(state[0].startDate, "yyyy-MM-dd"),
    format(state[0].endDate, "yyyy-MM-dd"),
  );

  const defaultAlignment =
    totalDays > 30 ? "month" : totalDays < 15 ? "date" : "week";
  const [alignment, setAlignment] = useState(defaultAlignment);

  useEffect(() => {
    setAlignment(totalDays > 30 ? "month" : totalDays < 15 ? "date" : "week");
  }, [totalDays]);

  const handleAlignmentChange = (e) => setAlignment(e.target.value);

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        adjustMenuRef.current &&
        !adjustMenuRef.current.contains(event.target)
      ) {
        setIsAdjustOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleApplyMetrics = (newMetrics) => {
    setSelectedMetrics(newMetrics);
    setIsMetricsModalOpen(false);
  };

  useEffect(() => {
    let RequestBody = apiRequestBodyBuilder();
    axios
      .post(
        "https://api.confidanto.com/campaign-group2/group-info",
        RequestBody,
      )
      .then((res) => setGroupType(res.data.type))
      .catch(console.error);
  }, []);

  const formatChartData = (data, isCompareMode, currentChartType, metrics) => {
    const grouped = data.reduce((acc, item) => {
      const date = item.date;
      acc[date] = acc[date] || {};

      metrics.forEach((m) => {
        const rawValue =
          typeof item[m] === "object" && item[m] !== null
            ? item[m].value
            : item[m];
        acc[date][m] = (acc[date][m] || 0) + (Number(rawValue) || 0);
      });

      return acc;
    }, {});

    const labels = Object.keys(grouped).sort();
    const chartInstance = chartRef.current;

    const datasets = metrics.map((m, i) => {
      const isPie = currentChartType === "pie";
      const metricColor =
        COLOR_MAP[m]?.border || DEFAULT_COLORS[i % DEFAULT_COLORS.length];

      let backgroundColor, borderColor;

      if (isPie) {
        backgroundColor = labels.map(
          (_, j) => DEFAULT_COLORS[j % DEFAULT_COLORS.length],
        );
        borderColor = "#ffffff";
      } else {
        borderColor = metricColor;

        // Create gradient fill matching reference image
        if (chartInstance && chartInstance.ctx) {
          const ctx = chartInstance.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          const start = COLOR_MAP[m]?.fillStart || "rgba(37, 99, 235, 0.2)";
          const end = COLOR_MAP[m]?.fillEnd || "rgba(37, 99, 235, 0.0)";
          gradient.addColorStop(0, start);
          gradient.addColorStop(1, end);
          backgroundColor = gradient;
        } else {
          backgroundColor = COLOR_MAP[m]?.fillStart || "rgba(37, 99, 235, 0.1)";
        }
      }

      // Route cost or high values to secondary yAxis (y1) to match right-hand axis display
      const yAxisID = m === "cost" ? "y1" : "y";

      return {
        label:
          ALL_METRICS.find((item) => item.key === m)?.title ||
          m.replace(/_/g, " ").toUpperCase(),
        data: labels.map((d) => grouped[d][m]?.toFixed(2)),
        borderColor,
        backgroundColor,
        fill: currentChartType === "line",
        pointBackgroundColor: borderColor,
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
        tension: 0.4,
        yAxisID,
      };
    });

    setChartData({ labels, datasets });
  };

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);

    const isDummy = customerId === "7948821642";
    const isCompareMode = state.length > 1;
    const metricsToFetch =
      chartType === "pie" ? [selectedMetrics[0]] : selectedMetrics;

    const body = {
      project_id: localStorage.getItem("project_id"),
      email: localStorage.getItem("email"),
      ad_account_id: localStorage.getItem("ad_account_id"),
      platform: localStorage.getItem("platform"),
      customer_id: localStorage.getItem("customer_id"),
      metrics: metricsToFetch,
      start_date: isDummy
        ? "2020-10-10"
        : format(state[0].startDate, "yyyy-MM-dd"),
      end_date: isDummy ? "2034-10-12" : format(state[0].endDate, "yyyy-MM-dd"),
      group_by: alignment,
      type: groupType,
    };

    if (isCompareMode) {
      body.previous_start_date = format(state[1].startDate, "yyyy-MM-dd");
      body.previous_end_date = format(state[1].endDate, "yyyy-MM-dd");
    }

    axios
      .post("https://api.confidanto.com/campaigns/chart", body, {
        signal: controller.signal,
      })
      .then((res) => {
        const data = res.data;
        if (!data?.length) return setIsDataAvailable(false);

        setIsDataAvailable(true);
        const normalizedData = isDummy
          ? data.map((i) => ({
              ...i,
              date: new Date(i.day).toISOString().split("T")[0],
            }))
          : data;

        if (isCompareMode) {
          const totals = normalizedData.reduce((acc, item) => {
            const date = item.date;
            acc[date] = acc[date] || {};
            metricsToFetch.forEach((m) => {
              acc[date][m] = acc[date][m] || { curr: 0, prev: 0 };
              acc[date][m].curr += item[`${m}_current`] || 0;
              acc[date][m].prev += item[`${m}_previous`] || 0;
            });
            return acc;
          }, {});

          const percentages = {};
          for (const d in totals) {
            percentages[d] = {};
            for (const m in totals[d]) {
              const { curr, prev } = totals[d][m];
              percentages[d][m] =
                prev > 0 ? ((curr - prev) / prev) * 100 : curr > 0 ? 100 : 0;
            }
          }
          setPercentageByDate(percentages);
        } else setPercentageByDate({});

        formatChartData(
          normalizedData,
          isCompareMode,
          chartType,
          metricsToFetch,
        );
      })
      .catch((err) => !axios.isCancel(err) && setIsDataAvailable(false))
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [
    customerId,
    selectedMetrics,
    alignment,
    state[0].startDate,
    state[0].endDate,
    state.length,
    groupType,
    chartType,
  ]);

  const lineBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false }, // Using custom legend above chart
      tooltip: {
        backgroundColor: "#1E293B",
        titleFont: { size: 12, weight: "600" },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (tooltipItem) => {
            const date = tooltipItem.chart.data.labels[tooltipItem.dataIndex];
            const key = tooltipItem.dataset.label
              .toLowerCase()
              .replace(/ /g, "_");
            const percent = percentageByDate[date]?.[key]?.toFixed(2);
            let label = `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
            if (percent !== undefined)
              label += ` (${percent >= 0 ? "+" : ""}${percent}%)`;
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: "#94A3B8", font: { size: 11 } },
        grid: { display: false },
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Clicks",
          color: "#64748B",
          font: { size: 11, weight: "600" },
        },
        ticks: {
          color: "#94A3B8",
          font: { size: 11 },
          callback: (value) => (value >= 1000 ? `${value / 1000}K` : value),
        },
        grid: { color: "#F1F5F9", borderDash: [4, 4] },
      },
      y1: {
        type: "linear",
        display: selectedMetrics.includes("cost"),
        position: "right",
        title: {
          display: true,
          text: "Cost (₹)",
          color: "#64748B",
          font: { size: 11, weight: "600" },
        },
        ticks: {
          color: "#94A3B8",
          font: { size: 11 },
          callback: (value) =>
            value >= 1000000
              ? `${value / 1000000}M`
              : value >= 1000
                ? `${value / 1000}K`
                : value,
        },
        grid: { drawOnChartArea: false },
      },
    },
  };

  return (
    <div
      className={`w-full ${isFullScreen ? "" : "bg-slate-50 max-h-screen p-4 md:p-8"} flex justify-center items-start`}
    >
      {/* Outer Card Container */}
      <div
        className={`w-full bg-white border border-slate-100 flex flex-col gap-6 transition-all duration-300 ${
          isFullScreen
            ? "fixed inset-0 z-50 p-4 sm:p-6 md:p-8 overflow-y-auto w-screen h-screen rounded-none shadow-xl"
            : "max-w-screen-2xl rounded-2xl shadow-sm p-6"
        }`}
      >
        {/* Header Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-base font-semibold text-slate-800">
            Performance Trends
          </h2>

          <div className="flex items-center gap-2">
            {/* Expand / Collapse Button */}
            <button
              onClick={toggleFullScreen}
              className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none transition-all cursor-pointer"
              title={isFullScreen ? "Collapse Chart" : "Expand Chart"}
            >
              {isFullScreen ? (
                <FaCompress className="text-xs text-slate-500" />
              ) : (
                <FaExpand className="text-xs text-slate-500" />
              )}
              <span className="hidden sm:inline">
                {isFullScreen ? "Collapse" : "Expand"}
              </span>
            </button>

            {/* Group By Selector */}
            <div className="relative">
              <select
                value={alignment}
                onChange={handleAlignmentChange}
                className="appearance-none bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-600 pl-8 pr-8 py-2 hover:bg-slate-50 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="month">Monthly</option>
                <option value="week">Weekly</option>
                <option value="date">Daily</option>
              </select>
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <svg
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5 pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {/* Adjust/Settings Menu Button */}
            <div className="relative" ref={adjustMenuRef}>
              <button
                onClick={() => setIsAdjustOpen(!isAdjustOpen)}
                className="p-2 border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                  />
                </svg>
              </button>

              {isAdjustOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-50 p-4">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                    Settings
                  </h3>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => {
                        setIsMetricsModalOpen(true);
                        setIsAdjustOpen(false);
                      }}
                      className="text-xs font-medium text-slate-700 hover:text-blue-600 border border-slate-200 rounded-lg p-2 text-left hover:bg-slate-50 transition-all flex items-center justify-between"
                    >
                      <span>Modify Metrics</span>
                      <svg
                        className="w-3.5 h-3.5 text-slate-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Custom Header Legend */}
        <div className="flex flex-wrap items-center gap-6">
          {chartData.datasets.map((ds, index) => (
            <div key={index} className="flex items-center gap-2">
              <span
                className="w-2.5 h-2.5 rounded-full inline-block"
                style={{ backgroundColor: ds.borderColor }}
              ></span>
              <span className="text-xs font-medium text-slate-600">
                {ds.label}
              </span>
            </div>
          ))}
        </div>

        {/* Chart Canvas Area */}
        <div
          className={`w-full relative ${isFullScreen ? "flex-1 min-h-[500px]" : "h-[380px]"}`}
        >
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full space-y-3">
              <CircularProgress size={36} sx={{ color: "#2563EB" }} />
              <p className="text-slate-400 text-xs font-medium">
                Loading performance data...
              </p>
            </div>
          ) : isDataAvailable ? (
            <Line ref={chartRef} data={chartData} options={lineBarOptions} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-slate-400 text-sm font-medium">
                No data available for the selected period.
              </p>
            </div>
          )}
        </div>
      </div>

      <MetricsModal
        isOpen={isMetricsModalOpen}
        onClose={() => setIsMetricsModalOpen(false)}
        allMetrics={ALL_METRICS}
        initialSelectedMetrics={selectedMetrics}
        onApply={handleApplyMetrics}
        selectionLimit={4}
      />
    </div>
  );
};

export default ChartPage;
